import { useEffect, useMemo, useRef, useState } from 'react';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type {
  OrderAutocompleteResponse,
  OrderDetail,
  OrderListItem,
  OrderListResponse,
  OrderLogEntry,
  OrderOverviewResponse,
  OrderPaginationResponse,
} from '@skupreme/api-contracts/core';
import OrdersService from '@utility/services/order';
import type { ApiError } from '@utility/services/base';
import {
  CommonConstant,
  ErrorMessageConstant,
  OrderStatus,
} from '@utility/constants/common';
import BatchOrdersService from '@utility/services/batch-order';
import { CommonUtility } from '@utility/common';
import { ToastMessage } from '@utility/toast';
import {
  buildOrderListParams,
  canFetchOrderList,
  type OrderListFilter,
} from '@features/orders/lib/list-params';
import { orderKeys } from '@features/orders/lib/query-keys';
import {
  getOrderListRevalidateInterval,
  getOrderRevalidateInterval,
  hasUnresolvedListIssue,
} from '@features/order-details';
import { useAsyncDebounce, useDebouncedEffect } from './debounceAsync';

const noBatches: any[] = [];
const noExports: any[] = [];

/* The public page shows each item through the catalog item card, which reads these fields. */
const refinePublicOrder = res => ({
  ...res,
  items: res.items.map(item => ({
    ...item,
    p_images: {
      display_image: item.images?.display_image,
    },
    content: {
      ...item,
      unit_price: item.price,
    },
  })),
});

export const orderPublicHook = token => {
  const queryClient = useQueryClient();
  const key = orderKeys.publicOrder(token);
  const query = useQuery({
    queryKey: key,
    queryFn: async () =>
      refinePublicOrder(await OrdersService.getPublicOrder(token)),
    enabled: !!token,
  });

  return {
    data: query.data ?? null,
    loading: query.isFetching,
    error: query.error
      ? query.error.message || ErrorMessageConstant.defaultAPIError
      : null,
    setDataAndRefine: res =>
      queryClient.setQueryData(key, refinePublicOrder(res)),
  };
};

const defaultOrderFilter: OrderListFilter = {
  search: '',
  sort: {
    time_acknowledged: CommonConstant.sortDirection.descending,
  },
};

/* One array for every empty list, so a render without rows is a stable value. */
const noOrders: OrderListItem[] = [];

type OrderListUpdate =
  | OrderListItem[]
  | ((previous: OrderListItem[]) => OrderListItem[]);

/* Services reject with ApiError: the server reason is in error, not message. */
function getApiErrorMessage(error: ApiError | null): string {
  if (!error) {
    return '';
  }

  return typeof error.error === 'string' && error.error
    ? error.error
    : ErrorMessageConstant.defaultAPIError;
}

/* One array for "nothing unsupported", so the state does not change on an empty answer. */
const noParams: string[] = [];

/*
 * EN-4117: the V2 list and /pagination answers name the query parameters the
 * API ignored. The set only grows: once a parameter is dropped from the request
 * the API no longer reports it, and forgetting it would send it again.
 */
const addNotSupported = (known: string[], answer: unknown): string[] => {
  const named = (answer as { not_supported?: unknown } | undefined)
    ?.not_supported;
  const fresh = Array.isArray(named)
    ? named.filter(
        (name): name is string =>
          typeof name === 'string' && !known.includes(name),
      )
    : [];

  return fresh.length ? [...known, ...fresh] : known;
};

export const orderHook = (status?: string, search?: string, isV2 = false) => {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<OrderListFilter>(defaultOrderFilter);
  const [reportedNotSupported, setReportedNotSupported] =
    useState<string[]>(noParams);
  const notSupported = isV2 ? reportedNotSupported : noParams;
  const rememberNotSupported = (answer: unknown) => {
    if (isV2) {
      setReportedNotSupported(known => addNotSupported(known, answer));
    }
  };

  /*
   * The query follows a settled copy of the filter, not every intermediate one.
   * Status travels with it so a route change cannot ask for the new list with
   * the old filter.
   */
  const [applied, setApplied] = useState({
    filter: defaultOrderFilter,
    search,
    status,
  });

  const filterChanged = (key: string, value: unknown) => {
    setFilter(previous => ({
      ...previous,
      page: 1,
      [key]: value,
    }));
  };

  /*
   * A narrowed list is read from its first page — but not on mount, where
   * this also fires: the page it should open on then is the URL's, and
   * `filterChanged` would reset it to 1 before that ever gets read.
   */
  const hasSetInitialLimit = useRef(false);
  useDebouncedEffect(
    () => {
      if (!status) {
        return;
      }
      if (!hasSetInitialLimit.current) {
        hasSetInitialLimit.current = true;
        return;
      }
      filterChanged('limit', 50);
    },
    [search],
    100,
  );

  /*
   * Read when the debounce runs out rather than when it started: a screen fills
   * its filter in over its first few renders, and a closed-over filter would be
   * out of date by then.
   */
  const pending = useRef({ filter, search, status });
  useEffect(() => {
    pending.current = { filter, search, status };
  });

  const settle = () => {
    setApplied(pending.current);
  };

  useDebouncedEffect(settle, [filter, search, status], 500);

  /*
   * A route change is followed at once, in the render that saw it, so the first
   * frame drawn is already the new list. It reads the page the previous queue
   * was on, which is why it cannot be an effect.
   */
  const [openQueue, setOpenQueue] = useState(status);
  if (openQueue !== status) {
    const start = { ...filter, page: 1, limit: 50 };

    setOpenQueue(status);
    setFilter(start);
    setApplied({ filter: start, search, status });
  }

  /* With nothing on screen yet the debounce is just a delay, so it is skipped. */
  const askable = canFetchOrderList(status, filter);
  const asked =
    applied.status === status &&
    canFetchOrderList(applied.status, applied.filter);

  useEffect(() => {
    if (askable && !asked) {
      settle();
    }
  });

  const params = useMemo(
    () =>
      buildOrderListParams(
        applied.filter,
        applied.status,
        applied.search,
        notSupported,
      ),
    [applied, notSupported],
  );

  const enabled = canFetchOrderList(applied.status, applied.filter);
  const listKey = orderKeys.list(params);

  /*
   * Placeholder rows preserve the table shape while a new key loads. The
   * returned loading flag turns them into skeletons, so they are never shown as
   * if they belonged to the requested page.
   */
  const keepWithinQueue = <T>(
    previous: T | undefined,
    previousStatus: string | undefined,
  ) => (previousStatus === params.status ? previous : undefined);

  const listQuery = useQuery<
    OrderListItem[],
    ApiError,
    OrderListItem[],
    ReturnType<typeof orderKeys.list>
  >({
    queryKey: listKey,
    queryFn: async ({ signal }): Promise<OrderListItem[]> => {
      const result: OrderListResponse = await OrdersService.get(params, signal);
      rememberNotSupported(result);

      return result.data ?? noOrders;
    },
    enabled,
    placeholderData: (previous, previousQuery) =>
      keepWithinQueue(previous, previousQuery?.queryKey[2].status),
    /*
     * The same stale banner the detail screen had, a row at a time: an
     * order_error resolved in Mongo reaches no client, so a table left open
     * kept flagging rows that were already cleared. The queue and its rows are
     * read off the key's own query, so a page or filter the screen has left
     * takes its interval with it rather than asking on behalf of the one on
     * show.
     */
    refetchInterval: query =>
      getOrderListRevalidateInterval(
        query.state.data,
        query.queryKey[2].status,
      ),
    /* Coming back to the tab is the other moment a row can be wrong. */
    refetchOnWindowFocus: query =>
      hasUnresolvedListIssue(query.state.data, query.queryKey[2].status),
  });

  /* Counts come from their own endpoint; only the rows hold up the table. */
  const countsQuery = useQuery<
    OrderPaginationResponse['data'],
    ApiError,
    OrderPaginationResponse['data'],
    ReturnType<typeof orderKeys.pageData>
  >({
    queryKey: orderKeys.pageData(params),
    queryFn: async ({ signal }) => {
      const result: OrderPaginationResponse = await OrdersService.pageData(
        params,
        signal,
      );
      rememberNotSupported(result);

      return result.data;
    },
    enabled,
    placeholderData: (previous, previousQuery) =>
      keepWithinQueue(previous, previousQuery?.queryKey[2].status),
  });

  /* Corrections a screen makes are written back into the cache. */
  const setData = (update: OrderListUpdate) => {
    queryClient.setQueryData<OrderListItem[]>(listKey, previous => {
      if (!previous) {
        return previous;
      }

      return typeof update === 'function' ? update(previous) : update;
    });
  };

  const refetch = () => {
    listQuery.refetch();
    countsQuery.refetch();
  };

  /*
   * Every cached page of every queue is stale once an order is created or
   * removed, not just the one on screen, so this drops the lot rather than
   * refetching the current key.
   */
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: orderKeys.lists() });
    queryClient.invalidateQueries({ queryKey: orderKeys.pageDatas() });
  };

  const pageChanged = (page: number) => {
    const nextFilter = {
      ...filter,
      page,
    };
    const nextApplied = { filter: nextFilter, search, status };

    pending.current = nextApplied;
    setFilter(nextFilter);
    setApplied(nextApplied);
  };

  const updateAllFilter = (
    patch: Record<string, unknown>,
    { resetPage = true }: { resetPage?: boolean } = {},
  ) => {
    setFilter(previous => ({
      ...previous,
      page: resetPage ? 1 : previous.page,
      ...patch,
    }));
  };

  const rows = listQuery.data;
  const counts = countsQuery.data;

  return {
    /* Undefined until counted: a nought here would read as an empty queue. */
    categoryCount: counts?.breakdown,
    data: rows ?? noOrders,
    error: getApiErrorMessage(listQuery.error),
    filter,
    filterChanged,
    invalidate,
    /* EN-4117: the params the V2 list request sends; undefined until it can be asked. */
    listParams: enabled ? params : undefined,
    loading:
      !enabled ||
      listQuery.isPlaceholderData ||
      (listQuery.isFetching && !rows),
    notSupported,
    pageChanged,
    refetch,
    setData,
    totalData: counts?.total_data ?? 0,
    totalPages: counts?.total_pages ?? 0,
    updateAllFilter,
  };
};

/*
 * The details page and the fulfillment form both ask for the same order, and
 * the form mounts only once the page has its answer — so a plain fetch per
 * component meant two requests and two copies that could drift apart. One
 * cache entry keeps them in step; the window is short enough that a fulfilled
 * order still refetches on a later visit.
 */
const ORDER_DETAIL_STALE_TIME = 30_000;

type OrderDetailUpdate =
  | OrderDetail
  | ((previous: OrderDetail | undefined) => OrderDetail | undefined);

export const orderDetailsHook = (orderId?: string) => {
  const queryClient = useQueryClient();
  const detailKey = orderKeys.detail(orderId);

  const query = useQuery<OrderDetail, ApiError>({
    queryKey: detailKey,
    queryFn: () => {
      if (!orderId) {
        throw new Error('Order ID is required');
      }

      return OrdersService.getById(orderId);
    },
    enabled: !!orderId,
    staleTime: ORDER_DETAIL_STALE_TIME,
    /*
     * An order_error is resolved in Mongo by a recovery the client is never
     * told about, so a screen left open kept showing a banner for an issue
     * that was already cleared. Ask again while one is on show, and stop the
     * moment it clears -- only a blocked order polls, and not for long.
     */
    refetchInterval: query => getOrderRevalidateInterval(query.state.data),
    /* Coming back to the tab is the other moment the banner can be wrong. */
    refetchOnWindowFocus: true,
  });

  const setData = (update: OrderDetailUpdate) => {
    queryClient.setQueryData<OrderDetail>(detailKey, previous =>
      typeof update === 'function' ? update(previous) : update,
    );
  };

  return {
    data: query.data,
    loading: query.isLoading,
    error: getApiErrorMessage(query.error),
    setData,
    refreshData: () => query.refetch(),
  };
};

type PageFilter = { limit: number; page: number; [key: string]: unknown };

const firstPage: PageFilter = {
  limit: CommonConstant.defaultPageSize,
  page: 1,
};

/* A page/limit filter with the setters the legacy tables call. */
const usePageFilter = () => {
  const [filter, setFilter] = useState<PageFilter>(firstPage);
  return {
    filter,
    pageChanged: page => setFilter(previous => ({ ...previous, page })),
    filterChanged: (key, value) =>
      setFilter(previous => ({ ...previous, page: 1, [key]: value })),
  };
};

export const orderFeedListHook = () => {
  const { filter, pageChanged, filterChanged } = usePageFilter();
  const query = useQuery({
    queryKey: orderKeys.feeds(filter),
    queryFn: () =>
      OrdersService.feedList({ ...filter, feedType: 'orders-import' }),
    placeholderData: keepPreviousData,
  });

  return {
    data: query.data?.data?.feeds,
    loading: query.isFetching,
    error: query.error?.message ?? '',
    refresh: () => query.refetch(),
    totalData: query.data?.totalCount ?? 0,
    totalPages: query.data?.totalPages ?? 0,
    filter,
    filterChanged,
    pageChanged,
  };
};

export const batchOrderHook = (status, search) => {
  const queryClient = useQueryClient();
  const finalSearch = useAsyncDebounce(search, 500);
  const batchStatus =
    status === OrderStatus.PendingShipment ? 'pending' : 'complete';
  const key = orderKeys.batches(batchStatus);
  const query = useQuery({
    queryKey: key,
    queryFn: ({ signal }) =>
      BatchOrdersService.get({ status: batchStatus }, signal),
    enabled: [OrderStatus.PendingShipment, OrderStatus.Shipped].includes(
      status,
    ),
  });

  const data = query.data?.data ?? noBatches;
  const filteredBatchData = useMemo(
    () =>
      finalSearch
        ? data.filter(batch =>
            (batch.orders || []).some(order =>
              order.marketplace_order_display_id.includes(finalSearch),
            ),
          )
        : data,
    [data, finalSearch],
  );

  return {
    filteredBatchData,
    data,
    totalData: query.data?.total_data ?? 0,
    loading: query.isFetching,
    error: (query.error as ApiError | null)?.code
      ? query.error?.message || ErrorMessageConstant.defaultAPIError
      : '',
    /* The list after a batch is renamed or removed. */
    setData: (next: any[]) =>
      queryClient.setQueryData(key, previous => ({
        ...(previous as object),
        data: next,
      })),
    updateData: () => query.refetch(),
  };
};

export const orderAddressSuggestionHook = (formData, shouldFetch = true) => {
  /*
   * Ask once the address has settled for a second, and only with a first
   * line. The form hands a new object on every render, so the text is what
   * settles: the same address sets the same string and renders nothing.
   */
  const settled = useAsyncDebounce(JSON.stringify(formData ?? null), 1000) as
    | string
    | undefined;
  const value = useMemo(() => {
    const address = settled ? JSON.parse(settled) : null;
    return address?.address_one ? (address as Record<string, unknown>) : null;
  }, [settled]);
  const query = useQuery({
    queryKey: orderKeys.addressSuggestion(value),
    queryFn: () =>
      OrdersService.addressSuggestion({
        ...value,
        postal_one: value?.postal_code,
      }),
    enabled: !!value && shouldFetch,
  });

  return {
    data: query.data ?? null,
    loading: query.isFetching,
    error: query.error
      ? query.error.message || ErrorMessageConstant.defaultAPIError
      : '',
  };
};

export const isValidOrderSearch = (search: unknown): search is string =>
  !!(search && typeof search === 'string');

type OrderAutocompleteOption = OrderAutocompleteResponse['data'][number] & {
  search: string;
};

const noSuggestions: OrderAutocompleteOption[] = [];

/* A V2 account's suggestions: the V2 orders search over every tab, so a suggestion opens an order the V2 page reads. */
const V2_SUGGESTION_LIMIT = 8;

const v2Suggestions = async (
  search: string,
  signal: AbortSignal,
): Promise<OrderAutocompleteOption[]> => {
  const result: OrderListResponse = await OrdersService.get(
    { search, page: 1, limit: V2_SUGGESTION_LIMIT },
    signal,
  );
  return (result.data ?? []).map(order => ({
    _id: order._id,
    marketplace_order_display_id: order.marketplace_order_display_id,
    fulfillment_status: order.fulfillment_status,
    search,
  })) as OrderAutocompleteOption[];
};

export const ordersAutocompleteHook = (search?: string, isV2 = false) => {
  const query = useQuery<OrderAutocompleteOption[], ApiError>({
    queryKey: [...orderKeys.autocomplete(search), isV2],
    queryFn: async ({ signal }): Promise<OrderAutocompleteOption[]> => {
      if (!isValidOrderSearch(search)) {
        return noSuggestions;
      }
      if (isV2) return v2Suggestions(search, signal);

      const result = await OrdersService.getAutocomplete({ search }, signal);

      /* Each suggestion carries the search it answers, for stale-check. */
      return result.data.map(d => ({ ...d, search }));
    },
    enabled: isValidOrderSearch(search),
  });

  return {
    data: query.data ?? noSuggestions,
    error: getApiErrorMessage(query.error),
    loading: query.isFetching,
  };
};

const noLogs: OrderLogEntry[] = [];

export const orderLogsHook = (orderId?: string) => {
  const query = useQuery<OrderLogEntry[], ApiError>({
    queryKey: orderKeys.logs(orderId),
    queryFn: async ({ signal }): Promise<OrderLogEntry[]> => {
      if (!orderId) {
        return noLogs;
      }

      const result = await OrdersService.getLogs(orderId, signal);

      return CommonUtility.isValidArray(result?.data) ? result.data : noLogs;
    },
    enabled: !!orderId,
  });

  return {
    data: query.data ?? noLogs,
    error: getApiErrorMessage(query.error),
    loading: !orderId || query.isFetching,
    refetch: query.refetch,
  };
};

export const ordersOverviewHook = () => {
  const query = useQuery<OrderOverviewResponse['data'], ApiError>({
    queryKey: orderKeys.overview(),
    queryFn: async ({ signal }) => {
      const result: OrderOverviewResponse =
        await OrdersService.getOrdersOverview(signal);

      return result.data;
    },
  });

  return {
    data: query.data,
    error: getApiErrorMessage(query.error),
    loading: query.isFetching,
    refetch: query.refetch,
  };
};

const exportError = error =>
  ToastMessage.error(error?.error || ErrorMessageConstant.defaultAPIError);

export const orderExportHook = () => {
  const { filter, pageChanged, filterChanged } = usePageFilter();
  const query = useQuery({
    queryKey: orderKeys.exports(filter),
    queryFn: async () => {
      try {
        return await OrdersService.exportOrderHistory(filter);
      } catch (error) {
        exportError(error);
        throw error;
      }
    },
    placeholderData: keepPreviousData,
  });
  const exporting = useMutation({
    mutationFn: ({ startDate, endDate, status }: Record<string, unknown>) =>
      OrdersService.exportOrder({
        start_date: startDate,
        end_date: endDate,
        status,
      }),
    onSuccess: res => {
      query.refetch();
      ToastMessage.success(res.data);
    },
    onError: exportError,
  });

  return {
    exportOrders: (startDate, endDate, status) =>
      exporting.mutateAsync({ startDate, endDate, status }).catch(() => {}),
    data: query.data?.data ?? noExports,
    loading: query.isFetching || exporting.isPending,
    fetchData: () => query.refetch(),
    totalData: query.data?.total_data ?? 0,
    totalPages: query.data?.total_pages ?? 0,
    filter,
    filterChanged,
    pageChanged,
  };
};
